import { useState } from 'react';
import { Alert, Col, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import type { EditModel } from 'edit/EditModel';
import { EditDialog } from 'edit/EditDialog';
import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { RetentionPolicyEditor } from 'edit/retentionPolicy/RetentionPolicyEditor';
import type { Zone } from 'api/zones/endpoints/postZone';

import type { EditableZone } from './EditableZone';

interface Props {
  readonly model: EditModel<EditableZone, Zone | null>;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

export const EditZoneDialog = ({ model, onSaved, onClose }: Props) => (
  <EditDialog model={model} onSaved={onSaved} onClose={onClose} entityName="Zone">
    {(entity) => <ZoneBody entity={entity} />}
  </EditDialog>
);

// `vstack gap-3` owns the field spacing so it holds whether the fields sit
// directly in the dialog body (create) or nested in a tab pane (edit) — the
// dialog body's own flex gap only sees this one wrapper.
const ZoneFormFields = observer(({ entity }: { readonly entity: EditableZone }) => (
  <div className="vstack gap-3">
    <Row>
      <Col md={12}>
        <EditString label="Name" value={entity.name} onChange={(v) => (entity.name = v)} mandatory />
      </Col>
    </Row>
    <Row>
      <Col md={12}>
        <EditText label="Description" value={entity.description} onChange={(v) => (entity.description = v)} />
      </Col>
    </Row>
    <RetentionPolicyEditor policies={entity.retentionPolicies} onAdd={entity.createRetentionPolicy} />
  </div>
));

// Edit mode adds an Audit Trail tab; create mode (no history yet) is just the
// form. The Tabs is wrapped so the dialog body sees a single child (its flex gap
// would otherwise land between the tab nav and content).
const ZoneBody = observer(({ entity }: { readonly entity: EditableZone }) => {
  const [key, setKey] = useState('zone');
  if (entity.isCreating) {
    return <ZoneFormFields entity={entity} />;
  }
  return (
    <div>
      <Tabs activeKey={key} onSelect={(k) => setKey(k ?? 'zone')} className="mb-3">
        <Tab eventKey="zone" title="Zone">
          <ZoneFormFields entity={entity} />
        </Tab>
        <Tab eventKey="audit" title="Audit Trail" mountOnEnter unmountOnExit>
          <ZoneAuditTrailTab entity={entity} />
        </Tab>
      </Tabs>
    </div>
  );
});

interface ZoneAuditDetails {
  readonly action?: string;
  readonly previous_state?: { readonly name?: string; readonly description?: string };
  readonly new_state?: { readonly name?: string; readonly description?: string };
}

// `audit_details` is freeform JSONB; extract the zone-shaped fields best-effort
// for display, checking each one (no blanket cast to the target shape).
const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const readState = (value: unknown): { name?: string; description?: string } => {
  if (typeof value !== 'object' || value === null) return {};
  const state = value as Record<string, unknown>;
  return { name: readString(state.name), description: readString(state.description) };
};
const asZoneAuditDetails = (details: unknown): ZoneAuditDetails => {
  if (typeof details !== 'object' || details === null) return {};
  const record = details as Record<string, unknown>;
  return { action: readString(record.action), previous_state: readState(record.previous_state), new_state: readState(record.new_state) };
};

const ZoneAuditTrailTab = observer(({ entity }: { readonly entity: EditableZone }) => {
  const state = entity.auditTrail;

  if (state === 'loading') {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" role="status" />
      </div>
    );
  }
  if (state instanceof Error) {
    return <Alert variant="danger">Failed to load audit trail: {state.message}</Alert>;
  }
  if (state.audit_trail.length === 0) {
    return <p className="text-muted mb-0">No audit trail available</p>;
  }

  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <th>Timestamp</th>
          <th>Type</th>
          <th>User</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {state.audit_trail.map((entry) => {
          const details = asZoneAuditDetails(entry.audit_details);
          return (
            <tr key={entry.audit_trail_id}>
              <td>{entry.created_at}</td>
              <td>{details.action ?? entry.audit_type}</td>
              <td>{entry.user_name}</td>
              <td>
                <b>Previous:</b> {details.previous_state?.name} / {details.previous_state?.description}
                <br />
                <b>New:</b> {details.new_state?.name} / {details.new_state?.description}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
});
